import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ShoppingCart, 
  Package, 
  CheckCircle, 
  Clock, 
  Headphones, 
  CreditCard, 
  Eye, 
  EyeOff, 
  Copy, 
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import SupportPin from '@/components/SupportPin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [clientOffer, setClientOffer] = useState(null);
  const [isAdminDirect, setIsAdminDirect] = useState(null);

  useEffect(() => {
    fetchOrders();
    // Fetch user info first, then only fetch active offer for direct clients
    axios.get(`${API}/auth/me`)
      .then(res => {
        const adminDirect = res.data?.created_by_admin_direct !== false;
        setIsAdminDirect(adminDirect);
        if (adminDirect) {
          // Only direct clients see client offers
          axios.get(`${API}/active-client-offer`)
            .then(r => { if (r.data?.id && r.data?.show_in_dashboard) setClientOffer(r.data); })
            .catch(() => {});
        }
      })
      .catch(() => setIsAdminDirect(true)); // fail-open
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const viewCredentials = (order) => {
    setSelectedOrder(order);
    setShowPassword(false);
    setCredentialsDialogOpen(true);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const activeOrders = orders.filter(o => o.status === 'active');
  const pendingOrders = orders.filter(o => o.status === 'pending');

  const handleStatClick = (stat) => {
    if (stat.path) {
      navigate(stat.path);
    }
  };

  const handleFeatureClick = (card) => {
    if (card.path) {
      navigate(card.path);
    } else {
      toast.info('Contact your reseller for support assistance');
    }
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  const stats = [
    {
      title: 'Total Services',
      value: orders.length,
      icon: Package,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      type: 'all',
      path: '/client/services'
    },
    {
      title: 'Active Services',
      value: activeOrders.length,
      icon: CheckCircle,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      type: 'active',
      path: '/client/services?status=active'
    },
    {
      title: 'Pending Services',
      value: pendingOrders.length,
      icon: Clock,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      type: 'pending',
      path: '/client/services?status=pending'
    },
    {
      title: 'Total Spent',
      value: `₹${orders.reduce((sum, order) => sum + (order.amount || 0), 0).toLocaleString()}`,
      icon: CreditCard,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-500',
      type: 'all',
      path: '/client/billing'
    }
  ];

  const featureCards = [
    {
      title: 'My Services',
      description: 'View all your active and pending services.',
      icon: Package,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      path: '/client/services'
    },
    {
      title: 'Support',
      description: 'Get help from your reseller for any issues.',
      icon: Headphones,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      path: '/client/support'
    },
    {
      title: 'Billing',
      description: 'View your billing history and invoices.',
      icon: CreditCard,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-500',
      path: '/client/billing'
    }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      suspended: 'bg-red-50 text-red-700 border-red-200',
      cancelled: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return styles[status] || styles.cancelled;
  };

  return (
    <DashboardLayout role="client">
      <div className="space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="mb-3">
          <h1 
            className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight" 
            data-testid="client-dashboard-title"
          >
            Client Dashboard
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">View and manage your active services</p>
        </div>

        {/* Active Client Offer Banner */}
        {clientOffer && (
          <div
            className="rounded-xl p-4 flex items-center justify-between gap-4"
            style={{
              background: clientOffer.bg_color || '#F0FFF4',
              border: `1.5px solid ${clientOffer.badge_color || '#22C55E'}`,
            }}
            data-testid="client-offer-dashboard-banner"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center text-xl"
                style={{ background: `${clientOffer.badge_color || '#22C55E'}20` }}>
                🏷️
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ color: clientOffer.text_color || '#0F1E3D' }}>
                  {clientOffer.title}
                </p>
                {clientOffer.description && (
                  <p className="text-xs mt-0.5" style={{ color: clientOffer.text_color || '#0F1E3D', opacity: 0.7 }}>
                    {clientOffer.description}
                  </p>
                )}
              </div>
            </div>
            {clientOffer.coupon_code && (
              <div className="flex-shrink-0 text-right">
                <p className="text-xs text-slate-500 mb-0.5">Coupon Code</p>
                <code className="px-3 py-1 rounded-lg text-sm font-bold tracking-widest"
                  style={{ background: `${clientOffer.badge_color || '#22C55E'}15`, color: clientOffer.badge_color || '#16A34A' }}>
                  {clientOffer.coupon_code}
                </code>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-48 sm:h-64" role="status" aria-label="Loading dashboard">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Stats Cards - Responsive Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 mb-4">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card 
                    key={stat.title} 
                    className="bg-white border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                    data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleStatClick(stat)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && handleStatClick(stat)}
                    aria-label={`${stat.title}: ${stat.value}. Click to view`}
                  >
                    <CardContent className="p-3 sm:p-4 lg:p-5">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 sm:space-y-2 lg:space-y-3 min-w-0 flex-1">
                          <p className="text-xs sm:text-sm font-medium text-slate-500 truncate">{stat.title}</p>
                          <p 
                            className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 truncate" 
                            data-testid={`stat-value-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {stat.value}
                          </p>
                          <p className="text-[10px] sm:text-xs text-slate-400 group-hover:text-blue-500 transition-colors hidden sm:block">
                            Click to view
                          </p>
                        </div>
                        <div className={`${stat.iconBg} p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0 ml-2`}>
                          <Icon className={`h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 ${stat.iconColor}`} aria-hidden="true" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Feature Cards - Responsive Grid */}
            <div className="space-y-4 sm:space-y-6 mb-6">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-800">Quick Access</h2>
                <p className="text-slate-500 mt-1 text-sm sm:text-base">Helpful resources and shortcuts.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {featureCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <Card 
                      key={card.title}
                      className="bg-white border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group h-auto"
                      data-testid={`feature-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => handleFeatureClick(card)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && handleFeatureClick(card)}
                      aria-label={`${card.title}: ${card.description}`}
                    >
                      <CardContent className="p-4 sm:p-5 lg:p-6">
                        <div className={`${card.iconBg} w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4`}>
                          <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${card.iconColor}`} aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold text-slate-800 mb-1 sm:mb-2 group-hover:text-blue-600 transition-colors text-sm sm:text-base">
                          {card.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-500 break-words whitespace-normal leading-relaxed">
                          {card.description}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Support PIN Section - Responsive max-width */}
            <div className="w-full max-w-md mb-6">
              <SupportPin />
            </div>

            {/* Filter Indicator - Responsive */}
            {filterStatus !== 'all' && (
              <div className="flex flex-wrap items-center gap-2 bg-blue-50 px-3 sm:px-4 py-2 rounded-lg">
                <span className="text-xs sm:text-sm text-slate-600">
                  Filtering by: <strong className="capitalize text-blue-600">{filterStatus}</strong>
                </span>
                <button 
                  onClick={() => setFilterStatus('all')}
                  className="text-xs sm:text-sm text-blue-600 hover:underline ml-2"
                  data-testid="clear-filter-btn"
                >
                  Clear filter
                </button>
              </div>
            )}

            {/* Services List - Responsive */}
            <div className="space-y-4">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2">
                {filterStatus === 'all' ? 'All Services' : `${filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Services`}
              </h2>

              {filteredOrders.length === 0 ? (
                <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="text-center py-12 sm:py-16">
                    <ShoppingCart className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-slate-200 mb-4" aria-hidden="true" />
                    <p className="text-slate-500 font-medium text-sm sm:text-base">No services yet</p>
                    <p className="text-xs sm:text-sm text-slate-400 mt-2 px-4 break-words whitespace-normal">
                      Your orders will appear here once purchased
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {filteredOrders.map((order) => (
                    <Card 
                      key={order.id} 
                      className="bg-white border-0 shadow-sm hover:shadow-md transition-all h-auto" 
                      data-testid={`order-card-${order.id}`}
                    >
                      <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-base sm:text-lg font-semibold text-slate-800 truncate flex-1">
                            {order.product_name}
                          </CardTitle>
                          <Badge variant="outline" className={`${getStatusBadge(order.status)} flex-shrink-0 text-xs`}>
                            {order.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6 pb-4 sm:pb-6">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs sm:text-sm text-slate-500">Amount</span>
                            <span className="font-semibold text-blue-600 text-sm sm:text-base">₹{order.amount?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs sm:text-sm text-slate-500">Billing Cycle</span>
                            <span className="text-xs sm:text-sm text-slate-700 capitalize">{order.billing_cycle?.replace('_', ' ')}</span>
                          </div>
                          {order.quantity > 1 && (
                            <div className="flex justify-between items-center">
                              <span className="text-xs sm:text-sm text-slate-500">Quantity</span>
                              <span className="text-xs sm:text-sm text-slate-700">{order.quantity}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Provisioning Info for Active Services */}
                        {order.status === 'active' && order.provisioning && (
                          <div className="pt-3 border-t border-slate-100">
                            <div className="flex items-center gap-2 mb-2">
                              <CheckCircle className="h-4 w-4 text-green-500" aria-hidden="true" />
                              <span className="text-xs sm:text-sm font-medium text-green-700">Service Active</span>
                            </div>
                            {order.provisioning.server_url && (
                              <div className="bg-slate-50 rounded-lg p-2 sm:p-3 space-y-1">
                                <p className="text-[10px] sm:text-xs text-slate-500">Server URL</p>
                                <p className="text-xs sm:text-sm font-mono text-slate-700 break-all">{order.provisioning.server_url}</p>
                              </div>
                            )}
                            <Button 
                              size="sm" 
                              className="w-full mt-3 text-xs sm:text-sm"
                              onClick={() => viewCredentials(order)}
                              data-testid={`view-credentials-${order.id}`}
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-2" aria-hidden="true" />
                              View Login Credentials
                            </Button>
                          </div>
                        )}
                        
                        {/* Pending Status Message */}
                        {order.status === 'pending' && (
                          <div className="pt-3 border-t border-slate-100">
                            <div className="flex items-center gap-2 text-amber-600">
                              <Clock className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                              <span className="text-xs sm:text-sm break-words whitespace-normal">Awaiting activation by CloudSetu</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Processing Status Message */}
                        {order.status === 'processing' && (
                          <div className="pt-3 border-t border-slate-100">
                            <div className="flex items-center gap-2 text-blue-600">
                              <Clock className="h-4 w-4 animate-spin flex-shrink-0" aria-hidden="true" />
                              <span className="text-xs sm:text-sm break-words whitespace-normal">Service is being provisioned...</span>
                            </div>
                          </div>
                        )}
                        
                        <div className="pt-3 border-t border-slate-100">
                          <p className="text-[10px] sm:text-xs text-slate-400">
                            Created: {new Date(order.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Credentials Dialog - Responsive */}
        <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-md mx-auto">
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg">Service Login Credentials</DialogTitle>
            </DialogHeader>
            {selectedOrder?.provisioning && (
              <ScrollArea className="max-h-[60vh] sm:max-h-none">
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" aria-hidden="true" />
                      <span className="font-medium text-green-800 text-sm sm:text-base truncate">{selectedOrder.product_name}</span>
                    </div>
                  </div>
                  
                  {selectedOrder.provisioning.server_url && (
                    <div className="space-y-1">
                      <label className="text-xs sm:text-sm font-medium text-slate-600">Server URL</label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-lg px-2 sm:px-3 py-2 font-mono text-xs sm:text-sm break-all">
                          {selectedOrder.provisioning.server_url}
                        </div>
                        <Button 
                          size="icon" 
                          variant="outline"
                          className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
                          onClick={() => copyToClipboard(selectedOrder.provisioning.server_url, 'Server URL')}
                          aria-label="Copy Server URL"
                        >
                          <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="outline"
                          className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
                          onClick={() => window.open(selectedOrder.provisioning.server_url, '_blank')}
                          aria-label="Open Server URL in new tab"
                        >
                          <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.provisioning.port && (
                    <div className="space-y-1">
                      <label className="text-xs sm:text-sm font-medium text-slate-600">Port</label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-lg px-2 sm:px-3 py-2 font-mono text-xs sm:text-sm">
                          {selectedOrder.provisioning.port}
                        </div>
                        <Button 
                          size="icon" 
                          variant="outline"
                          className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
                          onClick={() => copyToClipboard(selectedOrder.provisioning.port, 'Port')}
                          aria-label="Copy Port"
                        >
                          <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.provisioning.username && (
                    <div className="space-y-1">
                      <label className="text-xs sm:text-sm font-medium text-slate-600">Username</label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-lg px-2 sm:px-3 py-2 font-mono text-xs sm:text-sm break-all">
                          {selectedOrder.provisioning.username}
                        </div>
                        <Button 
                          size="icon" 
                          variant="outline"
                          className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
                          onClick={() => copyToClipboard(selectedOrder.provisioning.username, 'Username')}
                          aria-label="Copy Username"
                        >
                          <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.provisioning.password && (
                    <div className="space-y-1">
                      <label className="text-xs sm:text-sm font-medium text-slate-600">Password</label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-lg px-2 sm:px-3 py-2 font-mono text-xs sm:text-sm">
                          {showPassword ? selectedOrder.provisioning.password : '••••••••••'}
                        </div>
                        <Button 
                          size="icon" 
                          variant="outline"
                          className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Hide Password' : 'Show Password'}
                        >
                          {showPassword ? <EyeOff className="h-3 w-3 sm:h-4 sm:w-4" /> : <Eye className="h-3 w-3 sm:h-4 sm:w-4" />}
                        </Button>
                        <Button 
                          size="icon" 
                          variant="outline"
                          className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
                          onClick={() => copyToClipboard(selectedOrder.provisioning.password, 'Password')}
                          aria-label="Copy Password"
                        >
                          <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.provisioning.bios_ipmi_access && (
                    <div className="space-y-1">
                      <label className="text-xs sm:text-sm font-medium text-slate-600">BIOS/IPMI Access</label>
                      <div className="bg-slate-100 rounded-lg px-2 sm:px-3 py-2 text-xs sm:text-sm whitespace-pre-wrap break-words">
                        {selectedOrder.provisioning.bios_ipmi_access}
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-4 border-t border-slate-200 text-[10px] sm:text-xs text-slate-500">
                    <p>Provisioned on: {new Date(selectedOrder.provisioning.provisioned_at).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}</p>
                  </div>
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
